import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Chip, Tab, Tabs, Autocomplete } from '@mui/material';
import { Tune, Search } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { spAPI, inventoryAPI } from '../../services/api';
import DataTable from '../../components/common/DataTable';
import { toast } from 'react-toastify';

const AdjustDialog = ({ open, onClose, warehouses, onSaved }) => {
  const { register, handleSubmit, control, reset } = useForm({ defaultValues: { movement_type: 'in', quantity: 1 } });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (open) {
      reset({ movement_type: 'in', quantity: 1 });
      spAPI.getProducts({ limit: 1000 }).then(r => setProducts(r.data.data || [])).catch(() => {});
    }
  }, [open, reset]);

  const onSubmit = async (data) => {
    try { setLoading(true);
      await spAPI.adjustStock({ product_id: data.product_id, warehouse_id: data.warehouse_id,
        quantity: parseInt(data.quantity), movement_type: data.movement_type,
        unit_cost: data.unit_cost ? parseFloat(data.unit_cost) : null, notes: data.notes || null });
      toast.success('Stock adjusted'); reset(); onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Adjust Parts Stock</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller name="product_id" control={control} rules={{ required: 'Select a part' }}
                render={({ field, fieldState }) => (
                  <Autocomplete options={products} getOptionLabel={o => `${o.name} (${o.sku})`}
                    onChange={(_, v) => field.onChange(v ? v.id : '')}
                    renderInput={p => <TextField {...p} label="Part / Product *" size="small" error={!!fieldState.error} helperText={fieldState.error?.message || 'Search by name or SKU'} />} />
                )} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Warehouse *</InputLabel>
                <Select {...register('warehouse_id', { required: true })} label="Warehouse *" defaultValue="">
                  <MenuItem value="">— Select —</MenuItem>
                  {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type *</InputLabel>
                <Select {...register('movement_type')} label="Type *" defaultValue="in">
                  <MenuItem value="in">Stock In</MenuItem>
                  <MenuItem value="out">Stock Out</MenuItem>
                  <MenuItem value="adjustment">Adjustment</MenuItem>
                  <MenuItem value="return">Return</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Quantity *" type="number" size="small"
                {...register('quantity', { required: true, min: 1, valueAsNumber: true })} inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={6}><TextField fullWidth label="Unit Cost ($)" type="number" size="small" {...register('unit_cost')} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Notes" size="small" {...register('notes')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}
            sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
            {loading ? <CircularProgress size={20} /> : 'Adjust'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const SparePartsInventory = () => {
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0); const [page, setPage] = useState(0); const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState(''); const [tab, setTab] = useState(0);
  const [adjustOpen, setAdjustOpen] = useState(false); const [warehouses, setWarehouses] = useState([]);

  const fetchInventory = useCallback(async () => {
    try { setLoading(true);
      const { data } = await spAPI.getInventory({ page: page+1, limit: rowsPerPage, search: search||undefined, low_stock: tab===1?'true':undefined });
      setItems(data.data||[]); setTotal(data.pagination?.total||0);
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  }, [page, rowsPerPage, search, tab]);

  useEffect(() => {
    fetchInventory();
    inventoryAPI.getWarehouses().then(r => setWarehouses(r.data.data||[])).catch(()=>{});
  }, [fetchInventory]);

  const columns = [
    { key: 'sku', label: 'SKU', render: v => <Typography fontFamily="monospace" fontWeight={600} fontSize="0.78rem">{v}</Typography> },
    { key: 'product_name', label: 'Part Name', render: (v, row) => (
      <Box><Typography variant="body2" fontWeight={600}>{v}</Typography>
        <Typography variant="caption" color="text.secondary">{row.brand} · {row.category_name}</Typography></Box>
    )},
    { key: 'warehouse_name', label: 'Warehouse' },
    { key: 'unit_of_measure', label: 'UoM' },
    { key: 'quantity', label: 'In Stock', align: 'right', render: (v, row) => (
      <Chip label={`${v} ${row.unit_of_measure||''}`} size="small"
        color={v <= 0 ? 'error' : v <= row.reorder_level ? 'warning' : 'success'} />
    )},
    { key: 'reorder_level', label: 'Reorder At', align: 'right' },
    { key: 'selling_price', label: 'Price', align: 'right', render: v => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(v||0) },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Parts Inventory</Typography>
          <Typography variant="body2" color="text.secondary">{total} stock lines</Typography>
        </Box>
        <Button variant="contained" startIcon={<Tune />} onClick={() => setAdjustOpen(true)}
          sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>Adjust Stock</Button>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All Stock" /><Tab label="Low Stock Alerts" />
      </Tabs>
      <Box sx={{ mb: 3 }}>
        <TextField placeholder="Search by part name or SKU..." size="small" sx={{ minWidth: 320 }}
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Box>
      <DataTable columns={columns} rows={items} loading={loading} total={total} page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        emptyMessage={tab===1 ? 'All stock levels are healthy ✓' : 'No inventory records found'} />
      <AdjustDialog open={adjustOpen} onClose={() => setAdjustOpen(false)} warehouses={warehouses} onSaved={fetchInventory} />
    </Box>
  );
};

export default SparePartsInventory;
